import { Instant, LocalDate, LocalDateTime, Month, ZoneId } from "@js-joda/core";
import { useMutation, useQuery } from "@tanstack/react-query";
import assertNever from "assert-never";
import React from "react";
import useSWR from "swr";
import { CommCenterTicketCreateParams } from "../activities/AppNavigator";
import { Page } from "../activities/AppPage";
import {
  InitPlanOfCare,
  PlanOfCareItemWithAnswerAndIsOpen,
} from "../activities/VisitPlanOfCareActivity";
import { API } from "../API";
import useCaregiver from "../hooks/useCaregiver";
import useFilePicker from "../hooks/useFilePicker";
import useMixin from "../hooks/useMixin";
import usePatientsState from "../hooks/usePatientsState";
import { useUploadVerificationSelfieMutation } from "../hooks/useUploadVerificationSelfieMutation";
import { isLoading, loading, Loading } from "../Loading";
import { PatientDocumentTypeId, PatientTaskInstanceId, UnscheduleReason } from "../messages";
import { Geo } from "../messages/Geo";
import { DayTimeParam, PatientTaskInstance } from "../messages/PatientTasks";
import { PlanOfCareResponse, POCItem } from "../messages/PlanOfCare";
import { STRINGS } from "../strings";
import { AppState } from "../system/AppState";
import { getMinutesFromLocalTime, minutesToLocalTime } from "../time";
import { globalize, translate } from "../translate";
import { localDateMax } from "../utils/dateUtils";
import { fmap } from "../utils/fmap";
import { AndroidCameraOutlineIcon, GalleryThumbnailOutlineIcon } from "../utils/icones";
import { undefinedToNull } from "../utils/undefinedToNull";
import { withDefault } from "../utils/withDefault";
import BottomSheetMenu from "./BottomSheetMenu";
import ClockInOutModals from "./ClockInOutModals";
import ErrorScaffold from "./ErrorScaffold";
import AppModal, { AppModalProps } from "./Generic/AppModal/AppModal";
import { AppOverlay } from "./Generic/AppOverlay";
import LoadingScaffold from "./LoadingScaffold";
import NotFoundScaffold from "./NotFoundScaffold";
import { useCallNumber } from "./Patient/useCallNumber";
import PatientTaskInstanceScreen from "./PatientTaskInstanceScreen";
import { PatientTaskInstanceState } from "./PatientTaskInstanceTypes";
import UploadingModal from "./UploadingModal";
import useContactSupport from "./useContactSupport";
import useModal from "./useModal";
import useVisitInstanceClockInOut from "./useVisitInstanceClockInOut";
import UnscheduleReasonSelectActivity from "./UnscheduleReasonSelectActivity";

interface Props {
  api: API;
  appState: AppState;
  patientTaskInstanceId: PatientTaskInstanceId;
  initialScheduleDateTimePickerOpen: boolean;
  caregiverGeoLocation: Geo | Loading;
  onRequestNavigate: (page: Page) => void;
  onClickNewChatWithMedflyt: (params: CommCenterTicketCreateParams) => void;
  onClickGoBack: () => void;
}

export interface ScheduleTime {
  // Time in minutes between 0 and 24 * 60
  start: number;
  end: number;
}

export const DEFAULT_NO_ERROR_MODAL_PROPS: Omit<AppModalProps, "type"> = {
  isOpen: false,
  title: "",
  message: "",
  actions: [],
};

const PatientTaskInstanceActivity = (props: Props) => {
  const [patientTaskInstanceState, setPatientTaskInstanceState] =
    React.useState<PatientTaskInstanceState>({ type: "Loading" });
  const [isCallPatientOpen, setCallPatientOpen] = React.useState(false);
  const [isLocationModalOpen, setIsLocationModalOpen] = React.useState(false);
  const [isDirectionOpen, setIsDirectionOpen] = React.useState(false);
  const [isScheduleDateTimePickerOpen, setScheduleDateTimePickerOpen] = React.useState(
    props.initialScheduleDateTimePickerOpen
  );
  const [scheduleDateTime, setScheduleDateTime] = React.useState<Instant | null>(null);
  const [calendarViewYear, setCalendarViewYear] = React.useState<number | null>(null);
  const [calendarViewMonth, setCalendarViewMonth] = React.useState<Month | null>(null);
  const [scheduleDate, setScheduleDate] = React.useState<LocalDate | null>(null);
  const [scheduleTime, setScheduleTime] = React.useState<ScheduleTime | null>(null);
  const [isTimePickerShake, setTimePickerShake] = React.useState(false);
  const [planOfCare, setPlanOfCare] = React.useState<InitPlanOfCare | null>(null);
  const [errorModalProps, setErrorModalProps] = React.useState<Omit<AppModalProps, "type">>(
    DEFAULT_NO_ERROR_MODAL_PROPS
  );
  const nearFutureCaregiversQuery = useQuery({
    // ideally, the key would be per patientId, but
    // we can't access patientId here, so we use the patientTaskInstanceState instead.
    // to fix this, we need to refactor this component, and add an inner component
    // that has the resolved taskInstance, so we have access to the patientId.
    queryKey: ["near_future_caregivers", patientTaskInstanceState],
    enabled: patientTaskInstanceState.type === "Ready",
    queryFn: async () => {
      if (patientTaskInstanceState.type !== "Ready") {
        throw new Error("Task not ready");
      }

      const patientId = patientTaskInstanceState.patientTaskInstance.taskDetails.patientId;

      return props.api.getNearFutureCaregivers(patientId);
    },
  });

  const [minMinutes, setMinMinutes] = React.useState<number | undefined>(undefined);
  const [maxMinutes, setMaxMinutes] = React.useState<number | undefined>(undefined);
  const caregiver = useCaregiver(props.appState);
  const patientsState = usePatientsState(props.appState);
  const modal = useModal();
  const contactSupport = useContactSupport({
    api: props.api,
    appState: props.appState,
    onRequestNavigate: props.onRequestNavigate,
    onClickNewChatWithMedflyt: props.onClickNewChatWithMedflyt,
  });
  const handleUploadVerificationSelfie = useUploadVerificationSelfieMutation({
    api: props.api,
  });
  const [isAddDocumentBottomSheetOpen, setIsAddDocumentBottomSheetOpen] = React.useState(false);
  const uploadScannedDocument = useMutation({
    mutationFn: async (data: { title: string; base64: string }) => {
      if (patientTaskInstanceState.type !== "Ready") return;

      return props.api.uploadScannedDocumentByTaskInstanceId(
        patientTaskInstanceState.patientTaskInstance.id,
        { base64: data.base64, documentTitle: data.title, documentId: null, documentVersion: null }
      );
    },
    onSuccess: (data, params) => {
      if (patientTaskInstanceState.type !== "Ready" || data?.url === undefined) return;

      const newPatientTaskInstance: PatientTaskInstance = {
        ...patientTaskInstanceState.patientTaskInstance,
        taskDetails: {
          ...patientTaskInstanceState.patientTaskInstance.taskDetails,
          documents: [
            ...patientTaskInstanceState.patientTaskInstance.taskDetails.documents,
            {
              type: "PatientTaskInstanceDocumentScan",
              name: params.title,
              pdfUrl: data.url,
            },
          ],
        },
      };

      setPatientTaskInstanceState({
        type: "Ready",
        patientTaskInstance: newPatientTaskInstance,
      });

      props.appState.handlePatientTaskInstanceReceived(newPatientTaskInstance);
    },
    onError: () => {
      modal.setModalAsError({
        title: translate(STRINGS.ERROR),
        message: translate(STRINGS.SOMETHING_WENT_WRONG),
      });
    },
  });

  const createPatientNote = useMutation({
    mutationFn: (note: string) => props.api.postPatientNote(props.patientTaskInstanceId, note),
  });
  const { chooseFromGallery, takePhoto } = useFilePicker({
    onSelectFiles: (files) => {
      setIsAddDocumentBottomSheetOpen(false);
      uploadScannedDocument.mutate({ title: new Date().toISOString(), base64: files[0] });
    },
  });

  const callNumber = useCallNumber({ api: props.api, caregiver: caregiver });

  const handleRequireManualClock = () => {
    if (
      patientTaskInstanceState.type !== "Ready" ||
      patientTaskInstanceState.patientTaskInstance.type !== "SCHEDULED" ||
      patientTaskInstanceState.patientTaskInstance.visitInstance === null
    ) {
      return;
    }

    props.onRequestNavigate({
      type: "TimesheetActivity",
      visitInstanceId: patientTaskInstanceState.patientTaskInstance.visitInstance.id,
    });
  };

  const {
    canClockIn,
    canClockOut,
    canSubmitTimesheet,
    promptClockInMapModal,
    promptClockOutMapModal,
    clockInOutModalsState,
  } = useVisitInstanceClockInOut({
    onRequireManualClock: handleRequireManualClock,
    isCaregiverOfMedflyt: isLoading(props.appState.appCaregiverState)
      ? false
      : props.appState.appCaregiverState.caregiver.isCaregiverOfMedFlyt,
    visitInstance:
      patientTaskInstanceState.type === "Ready" &&
      patientTaskInstanceState.patientTaskInstance.type === "SCHEDULED" &&
      patientTaskInstanceState.patientTaskInstance.visitInstance !== null
        ? patientTaskInstanceState.patientTaskInstance.visitInstance
        : loading(),
    caregiverId: isLoading(caregiver) ? loading() : caregiver.id,
    api: props.api,
    appState: props.appState,
    onUploadVerificationSelfie: (visitInstanceId, imageBase64, clockType) =>
      handleUploadVerificationSelfie.mutateAsync({
        clockType,
        visitInstanceId,
        imageBase64,
      }),
    onRequestMissedVisit: async (visitInstanceId) => {
      await props.api.caregiverMarkVisitAsMissed(visitInstanceId);
      props.onClickGoBack();
    },
  });

  const { appTasksState } = useMixin(props.appState);

  const initMinAndMaxByScheduleDate = React.useCallback((taskInstance: PatientTaskInstance) => {
    if (taskInstance.dayTimeParams !== null && taskInstance.scheduleDateTime !== null) {
      const dayTimeOfDay = taskInstance.dayTimeParams.find((dayTimeParam) =>
        dayTimeParam.dayOfWeek.equals(
          LocalDateTime.ofInstant(taskInstance.scheduleDateTime).dayOfWeek()
        )
      );
      if (dayTimeOfDay !== undefined) {
        updateMinAndMax(dayTimeOfDay);
      }
    }
  }, []);

  React.useEffect(() => {
    if (patientTaskInstanceState.type === "Ready") {
      initMinAndMaxByScheduleDate(patientTaskInstanceState.patientTaskInstance);
    }
  }, [initMinAndMaxByScheduleDate, patientTaskInstanceState]);

  const handleClickOpenCallPatient = React.useCallback(() => setCallPatientOpen(true), []);
  const handleRequestCloseCallPatient = React.useCallback(() => setCallPatientOpen(false), []);
  const handleClickOpenScheduleDateTimePicker = React.useCallback(() => {
    if (!isLoading(caregiver) && caregiver.isCaregiverOfMedFlyt) {
      props.onRequestNavigate({
        type: "PatientTaskInstanceChange",
        patientTaskInstanceId: props.patientTaskInstanceId,
      });
    } else {
      setScheduleDateTimePickerOpen(true);
    }
  }, [caregiver, props]);
  const handleRequestCloseScheduleDateTimePicker = React.useCallback(
    () => setScheduleDateTimePickerOpen(false),
    []
  );
  const handleSetCalendarViewYear = React.useCallback(
    (year: number) => setCalendarViewYear(year),
    []
  );
  const handleSetCalendarViewMonth = React.useCallback(
    (month: Month) => setCalendarViewMonth(month),
    []
  );
  const handleSetScheduleTime = React.useCallback(
    (time: ScheduleTime) => setScheduleTime(time),
    []
  );
  const handleClearErrorModal = React.useCallback(
    () => setErrorModalProps(DEFAULT_NO_ERROR_MODAL_PROPS),
    []
  );

  const handleClickCallPatient = (phoneNumber: string): void => {
    const link = document.createElement("a");
    link.href = "tel:" + phoneNumber;
    link.click();
  };

  const handleSetScheduleDate = React.useCallback(
    (date: LocalDate) => {
      if (patientTaskInstanceState.type !== "Ready") {
        throw new Error("Task not ready");
      }

      const startDate = localDateMax(
        LocalDate.now(),
        patientTaskInstanceState.patientTaskInstance.startDate
      );

      if (date.isBefore(startDate)) {
        const startDateStr = globalize()
          .formatLocalDate(startDate, {
            date: "medium",
          })
          .split(",")[0];

        setErrorModalProps({
          isOpen: true,
          title: translate(STRINGS.WRONG_DATES_SELECTION),
          message: `${translate(STRINGS.THE_SELECTED_DATE_IS_EARLIER_THAN)}`.replace(
            "{date}",
            startDateStr
          ),
          actions: [
            {
              key: "OK",
              variant: "soft",
              color: "primary",
              children: "OK",
              onClick: handleClearErrorModal,
            },
          ],
        });
        return;
      }
      if (patientTaskInstanceState.patientTaskInstance.dayTimeParams !== null) {
        const dayTimeParam = patientTaskInstanceState.patientTaskInstance.dayTimeParams.find(
          (x) => x.dayOfWeek.name() === date.dayOfWeek().name()
        );
        if (dayTimeParam !== undefined) {
          updateMinAndMax(dayTimeParam);
        }
      }
      setScheduleDate(date);
    },
    [handleClearErrorModal, patientTaskInstanceState]
  );

  const updateMinAndMax = (dayTimeParam: DayTimeParam) => {
    const fromMinute = dayTimeParam.startTime.hour() * 60 + dayTimeParam.startTime.minute();
    const toMinute = dayTimeParam.endTime.hour() * 60 + dayTimeParam.endTime.minute();
    setMinMinutes(fromMinute);
    setMaxMinutes(toMinute);
  };

  const parseScheduleParamsToInstant = (date: LocalDate, time: ScheduleTime): Instant => {
    const ldt = date.atTime(minutesToLocalTime(time.start));
    return ldt.atZone(ZoneId.systemDefault()).toInstant();
  };

  const getScheduleDateAndTimeFromInstantAndDuration = (
    timeInstant: Instant,
    durationMinutes: number
  ): {
    date: LocalDate;
    time: ScheduleTime;
  } => {
    const ldtScheduleDateTime = LocalDateTime.ofInstant(timeInstant);
    const startTime = getMinutesFromLocalTime(ldtScheduleDateTime.toLocalTime());
    return {
      date: ldtScheduleDateTime.toLocalDate(),
      time: {
        start: startTime,
        end: startTime + durationMinutes,
      },
    };
  };

  const handleRequestApproveScheduleDateTime = React.useCallback(() => {
    if (patientTaskInstanceState.type !== "Ready") {
      throw new Error("Task not ready");
    }

    if (scheduleDate === null) {
      throw new Error("Missing task schedule date");
    }

    if (scheduleTime === null) {
      setTimePickerShake(true);
      return;
    }

    const newScheduleDateTime: Instant = parseScheduleParamsToInstant(scheduleDate, scheduleTime);
    const newDuration = scheduleTime.end - scheduleTime.start;
    const params = {
      scheduleDateTime: newScheduleDateTime,
      duration: newDuration,
    };

    props.api
      .updatePatientTaskInstanceScheduleDateTime(props.patientTaskInstanceId, params)
      .then((res) => {
        const newPatientTaskInstanceState: PatientTaskInstanceState = {
          ...patientTaskInstanceState,
        };
        if (patientTaskInstanceState.patientTaskInstance.type === "PENDING") {
          newPatientTaskInstanceState.patientTaskInstance.type = "SCHEDULED";
          // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
          if (newPatientTaskInstanceState.patientTaskInstance.type === "SCHEDULED") {
            newPatientTaskInstanceState.patientTaskInstance.visitInstance = res.visitInstance;
          }
        }
        newPatientTaskInstanceState.patientTaskInstance.scheduleDateTime = newScheduleDateTime;
        newPatientTaskInstanceState.patientTaskInstance.taskDetails.durationMinutes = newDuration;

        const dateAndTime = getScheduleDateAndTimeFromInstantAndDuration(
          newScheduleDateTime,
          newDuration
        );

        setPatientTaskInstanceState(newPatientTaskInstanceState);
        setScheduleDateTime(newScheduleDateTime);
        setScheduleDate(dateAndTime.date);
        setScheduleTime(dateAndTime.time);
        setScheduleDateTimePickerOpen(false);
        props.appState.handlePatientTaskInstanceReceived(
          newPatientTaskInstanceState.patientTaskInstance
        );
      });
  }, [
    patientTaskInstanceState,
    props.api,
    props.appState,
    props.patientTaskInstanceId,
    scheduleDate,
    scheduleTime,
  ]);

  const updatePatientTaskUnscheduleWithReason = useMutation({
    mutationFn: (reason: UnscheduleReason) =>
      props.api.updatePatientTaskUnscheduleWithReason(props.patientTaskInstanceId, reason),
    onSuccess: () => {
      modal.setModalAsSuccess({
        title: translate(STRINGS.TASK_WAS_REMOVED_FROM_YOUR_SCHEDULE),
        closeText: translate(STRINGS.OK),
      });
    },
    onError: (error) => {
      modal.setModalAsError({
        title: translate(STRINGS.ERROR),
        message: translate(STRINGS.SOMETHING_WENT_WRONG),
      });
      console.error("Error unscheduling task", error);
    },
  });

  const updatePatientTaskUnschedule = React.useCallback(
    async (reason: UnscheduleReason) => {
      if (patientTaskInstanceState.type !== "Ready") {
        console.error("Task not ready");
        return;
      }
      modal.setModalAsLoading();
      updatePatientTaskUnscheduleWithReason.mutate(reason);
      const newPatientTaskInstanceState: PatientTaskInstanceState = { ...patientTaskInstanceState };
      if (patientTaskInstanceState.patientTaskInstance.type === "SCHEDULED") {
        newPatientTaskInstanceState.patientTaskInstance.type = "PENDING";
        newPatientTaskInstanceState.patientTaskInstance.scheduleDateTime = null;
        newPatientTaskInstanceState.patientTaskInstance.visitInstanceId = null;
      }
      setScheduleDateTime(null);
      setScheduleDate(null);
      setScheduleTime(null);
      setPatientTaskInstanceState(newPatientTaskInstanceState);
      props.appState.handlePatientTaskInstanceReceived(
        newPatientTaskInstanceState.patientTaskInstance
      );
    },
    [modal, patientTaskInstanceState, props.appState, updatePatientTaskUnscheduleWithReason]
  );

  const handleUnscheduleClick = React.useCallback(() => {
    modal.setModalAsPrompt({
      title: translate(STRINGS.REMOVE_TASK),
      message: `${translate(STRINGS.WHAT_IS_THE_REASON_YOU_WANT_TO_REMOVE_THE_TASK)}?`,
      closeText: translate(STRINGS.I_HAVE_CHANGED_MY_MIND),
      actions: [
        {
          key: "unschedule",
          variant: "soft",
          color: "white",
          style: {
            height: "auto",
            display: "flex",
            flexDirection: "column",
            gap: "10px",
          },
          children: (
            <UnscheduleReasonSelectActivity
              api={props.api}
              patientTaskInstanceId={props.patientTaskInstanceId}
              onValidSubmit={updatePatientTaskUnschedule}
            />
          ),
        },
      ],
    });
  }, [modal, props.api, props.patientTaskInstanceId, updatePatientTaskUnschedule]);

  const onClickDisabled = React.useCallback(() => {
    setErrorModalProps({
      isOpen: true,
      title: translate(STRINGS.WHOOPS),
      message: translate(STRINGS.YOU_CANT_EDIT_VISIT_WITH_CICO),
      actions: [
        {
          key: "OK",
          variant: "soft",
          color: "primary",
          children: "OK",
          onClick: handleClearErrorModal,
        },
      ],
    });
  }, [handleClearErrorModal]);

  const handleClickRegularDocument = React.useCallback(
    (documentId: PatientDocumentTypeId): void => {
      if (patientTaskInstanceState.type === "Ready") {
        props.onRequestNavigate.call(null, {
          type: "PatientTaskInstanceDocument",
          patientId: patientTaskInstanceState.patientTaskInstance.taskDetails.patientId,
          patientTaskInstanceId: props.patientTaskInstanceId,
          patientDocumentId: documentId,
        });
      }
    },
    [patientTaskInstanceState, props.onRequestNavigate, props.patientTaskInstanceId]
  );

  const handleClickScannedDocument = React.useCallback(
    (name: string, pdfUrl: string): void => {
      props.onRequestNavigate.call(null, {
        type: "DocViewerScreen",
        title: name,
        pdfUrl: pdfUrl,
      });
    },
    [props.onRequestNavigate]
  );

  const parsePlanOfCare = (pocItems: POCItem[]): PlanOfCareItemWithAnswerAndIsOpen[] => {
    const toReturn: PlanOfCareItemWithAnswerAndIsOpen[] = pocItems.map((value) => {
      return {
        item: value,
        answer: null,
        isOpen: false,
      };
    });
    return toReturn;
  };

  const setPlanOfCareByResponse = React.useCallback((poc: PlanOfCareResponse): void => {
    if (poc.planOfCareWithAnswers === null) {
      setPlanOfCare(null);
    } else {
      setPlanOfCare({
        initialPlanOfCare: {
          list: parsePlanOfCare(poc.planOfCareWithAnswers.planOfCareItems),
          planOfCareType: poc.planOfCareWithAnswers.planOfCareType,
          poc: poc.planOfCareWithAnswers.poc,
        },
        initialDocument: poc.planOfCareWithAnswers.docItems,
        initialCaregiverSignature: fmap(
          poc.planOfCareWithAnswers.poc.caregiverSignature,
          (x) => x.data
        ),
        initialPatientSignature: fmap(
          poc.planOfCareWithAnswers.poc.patientSignature,
          (x) => x.data
        ),
        patientUnsignedReason: undefinedToNull(poc.planOfCareWithAnswers.poc.patientUnsignedReason),
      });
    }
  }, []);

  const updatePlanOfCare = React.useCallback(() => {
    props.api.getTaskPlanOfCare(props.patientTaskInstanceId).then((res: PlanOfCareResponse) => {
      setPlanOfCareByResponse(res);

      if (
        fmap(res.planOfCareWithAnswers, (x) => x.poc.submittedAt) !== null &&
        patientTaskInstanceState.type === "Ready"
      ) {
        const newPatientTaskInstanceState: PatientTaskInstanceState = {
          ...patientTaskInstanceState,
        };
        newPatientTaskInstanceState.patientTaskInstance.taskDetails.planOfCareCompleted = true;
        props.appState.handlePatientTaskInstanceReceived(
          newPatientTaskInstanceState.patientTaskInstance
        );
      }
    });
  }, [
    props.api,
    props.patientTaskInstanceId,
    props.appState,
    setPlanOfCareByResponse,
    patientTaskInstanceState,
  ]);

  const handleClickPlanOfCare = React.useCallback((): void => {
    if (planOfCare !== null) {
      props.onRequestNavigate.call(null, {
        type: "TaskPlanOfCare",
        taskInstanceId: props.patientTaskInstanceId,
        init: planOfCare,
        updatePlanOfCare: updatePlanOfCare,
      });
    }
  }, [updatePlanOfCare, planOfCare, props.onRequestNavigate, props.patientTaskInstanceId]);

  const handleTimePickerShakeAnimationEnd = React.useCallback((): void => {
    setTimePickerShake(false);
  }, []);

  const calendarViewMonthWithDefault = React.useMemo((): Month => {
    if (calendarViewMonth !== null) {
      return calendarViewMonth;
    }
    if (patientTaskInstanceState.type === "Ready") {
      const startDate = localDateMax(
        LocalDate.now(),
        patientTaskInstanceState.patientTaskInstance.startDate
      );

      return startDate.month();
    }
    return LocalDate.now().month();
  }, [calendarViewMonth, patientTaskInstanceState]);

  useSWR(
    `patient_task_instances/${props.patientTaskInstanceId}/plan_of_care`,
    () => props.api.getTaskPlanOfCare(props.patientTaskInstanceId),
    { onSuccess: (res) => setPlanOfCareByResponse(res) }
  );

  const handleClickLocationModal = React.useCallback(() => {
    setIsLocationModalOpen(true);
  }, []);

  const handleRequestCloseLocationModal = React.useCallback(() => {
    setIsLocationModalOpen(false);
  }, []);

  const handleClickDirections = React.useCallback(() => {
    setIsDirectionOpen(true);
    setIsLocationModalOpen(false);
  }, []);

  const handleRequestCloseDirection = React.useCallback(() => {
    setIsDirectionOpen(false);
  }, []);

  const handleClickLinkedPatient = React.useCallback(() => {
    if (patientTaskInstanceState.type === "Ready") {
      if (
        patientTaskInstanceState.patientTaskInstance.taskDetails.linkedPatientTaskInstanceId !==
        null
      ) {
        props.onRequestNavigate({
          type: "PatientTaskInstance",
          patientTaskInstanceId:
            patientTaskInstanceState.patientTaskInstance.taskDetails.linkedPatientTaskInstanceId,
          initialScheduleDateTimePickerOpen: false,
        });
      }
    }
  }, [patientTaskInstanceState, props]);

  const scheduleLocalDateTime = React.useMemo(() => {
    return scheduleDateTime === null ? null : LocalDateTime.ofInstant(scheduleDateTime);
  }, [scheduleDateTime]);

  React.useEffect(() => {
    if (!isLoading(appTasksState)) {
      const patientTaskInstance = appTasksState.tasks.find(
        (x) => x.id === props.patientTaskInstanceId
      );

      if (patientTaskInstance === undefined) {
        setPatientTaskInstanceState({ type: "NotFound" });
        return;
      }

      setPatientTaskInstanceState({
        type: "Ready",
        patientTaskInstance: patientTaskInstance,
      });
      setScheduleDateTime(patientTaskInstance.scheduleDateTime);
      if (patientTaskInstance.scheduleDateTime !== null) {
        const dateAndTime = getScheduleDateAndTimeFromInstantAndDuration(
          patientTaskInstance.scheduleDateTime,
          patientTaskInstance.taskDetails.durationMinutes !== null
            ? patientTaskInstance.taskDetails.durationMinutes
            : 12 * 60
        );
        setScheduleDate(dateAndTime.date);
        setScheduleTime(dateAndTime.time);
        setCalendarViewYear(dateAndTime.date.year());
        setCalendarViewMonth(dateAndTime.date.month());
      }
    }
  }, [appTasksState, props.patientTaskInstanceId]);

  if (isLoading(patientsState)) {
    return <LoadingScaffold onClickGoBack={props.onClickGoBack} />;
  }

  if (nearFutureCaregiversQuery.isLoading) {
    return <LoadingScaffold onClickGoBack={props.onClickGoBack} />;
  }

  if (nearFutureCaregiversQuery.isError) {
    return (
      <ErrorScaffold
        title={translate(STRINGS.ERROR)}
        message={translate(STRINGS.SOMETHING_WENT_WRONG)}
        onClickGoBack={props.onClickGoBack}
      />
    );
  }

  switch (patientTaskInstanceState.type) {
    case "Loading":
      return <LoadingScaffold onClickGoBack={props.onClickGoBack} />;
    case "NotFound":
      return <NotFoundScaffold onClickGoBack={props.onClickGoBack} />;
    case "Ready": {
      if (isLoading(props.caregiverGeoLocation)) {
        return <LoadingScaffold onClickGoBack={props.onClickGoBack} />;
      }

      const startDate = localDateMax(
        LocalDate.now(),
        patientTaskInstanceState.patientTaskInstance.startDate
      );

      const patientFromState = patientsState.patients.find(
        (x) => x.id === patientTaskInstanceState.patientTaskInstance.taskDetails.patientId
      );

      if (patientFromState === undefined) {
        return <NotFoundScaffold onClickGoBack={props.onClickGoBack} />;
      }

      return (
        <>
          <PatientTaskInstanceScreen
            onClickCreatePatientNote={createPatientNote.mutate}
            patient={patientFromState}
            patientTaskInstance={patientTaskInstanceState.patientTaskInstance}
            scheduleDateTime={scheduleLocalDateTime}
            isCallPatientOpen={isCallPatientOpen}
            calendarViewYear={withDefault(calendarViewYear, startDate.year())}
            calendarViewMonth={calendarViewMonthWithDefault}
            scheduleDate={scheduleDate}
            scheduleTime={scheduleTime}
            calendarMinDate={startDate}
            isScheduleDateTimePickerOpen={isScheduleDateTimePickerOpen}
            isTimePickerShake={isTimePickerShake}
            nearFutureCaregivers={nearFutureCaregiversQuery.data.caregivers}
            errorModalProps={errorModalProps}
            isLocationModalOpen={isLocationModalOpen}
            isDirectionsOpen={isDirectionOpen}
            minMinutes={minMinutes}
            maxMinutes={maxMinutes}
            canClockIn={canClockIn()}
            canClockOut={canClockOut()}
            canSubmitTimesheet={canSubmitTimesheet()}
            onClickClockIn={promptClockInMapModal}
            onClickClockOut={promptClockOutMapModal}
            onClickSubmitTimesheet={handleRequireManualClock}
            onClickLocationModal={handleClickLocationModal}
            onClickDirections={handleClickDirections}
            onRequestCloseLocationModal={handleRequestCloseLocationModal}
            onRequestCloseDirections={handleRequestCloseDirection}
            onTimePickerShakeAnimationEnd={handleTimePickerShakeAnimationEnd}
            onClickOpenCallPatient={handleClickOpenCallPatient}
            onClickCallPatient={handleClickCallPatient}
            onClickOpenScheduleDateTimePicker={handleClickOpenScheduleDateTimePicker}
            onRequestCloseScheduleDateTimePicker={handleRequestCloseScheduleDateTimePicker}
            onRequestCloseCallPatient={handleRequestCloseCallPatient}
            onRequestChangeCalendarViewMonth={handleSetCalendarViewMonth}
            onRequestChangeCalendarViewYear={handleSetCalendarViewYear}
            onRequestChangeScheduleDate={handleSetScheduleDate}
            onRequestChangeScheduleTime={handleSetScheduleTime}
            onRequestApproveScheduleDateTime={handleRequestApproveScheduleDateTime}
            onClickRegularDocument={handleClickRegularDocument}
            onClickScannedDocument={handleClickScannedDocument}
            onClickPlanOfCare={handleClickPlanOfCare}
            onClickBack={props.onClickGoBack}
            onClickUnscheduleTask={handleUnscheduleClick}
            onClickCall={callNumber}
            onClickAddDocument={() => setIsAddDocumentBottomSheetOpen(true)}
            caregiverGeoLocation={props.caregiverGeoLocation}
            onRequestNavigate={props.onRequestNavigate}
            onclickDisabled={onClickDisabled}
            onRequestOutsourcingAgencyData={props.api.getOutsourcingAgencyData}
            onClickLinkedPatient={handleClickLinkedPatient}
          />
          {isLoading(contactSupport) ? null : (
            <AppOverlay>
              <ClockInOutModals {...clockInOutModalsState} contactSupportProps={contactSupport} />
            </AppOverlay>
          )}
          <AppModal {...modal.modalProps} />
          <UploadingModal isOpen={uploadScannedDocument.isLoading} />
          <BottomSheetMenu
            isOpen={isAddDocumentBottomSheetOpen}
            title={translate(STRINGS.HOW_WOULD_YOU_LIKE_TO_UPLOAD_THE_DOCUMENT)}
            items={[
              {
                title: translate(STRINGS.SCAN_A_PHOTO),
                leading: <AndroidCameraOutlineIcon height="20" width="20" />,
                onClick: takePhoto,
              },
              {
                title: translate(STRINGS.UPLOAD_FROM_GALLERY),
                leading: <GalleryThumbnailOutlineIcon height="20" width="20" />,
                onClick: chooseFromGallery,
              },
            ]}
            onClose={() => setIsAddDocumentBottomSheetOpen(false)}
          />
        </>
      );
    }
    default:
      assertNever(patientTaskInstanceState);
  }
};

export default PatientTaskInstanceActivity;
